<template>
    <div
        :class="['scope-selector', `-${size}`]"
        :style="styles"
        ref="selectorRef"
    >
        <div class="scope-selector__highlight" :style="highlightStyle" />

        <button
            v-for="(option, index) in options"
            :key="index"
            :ref="(el) => setOptionRef(el, index)"
            @click.prevent.stop="select(option)"
            :disabled="disabled"
            :class="[
                'scope-selector__option',
                option.id,
                {
                    '-selected': isSelected(option),
                    '-iconic': icons
                }
            ]"
        >
            <template v-if="icons">
                <Icon :icon="getOptionLabel(option)" />
            </template>
            <template v-else>
                <Icon
                    v-if="getOptionIcon(option)"
                    :icon="getOptionIcon(option)"
                    class="scope-selector__option-icon"
                />
                <span class="scope-selector__option-label">
                    {{ getOptionLabel(option) }}
                </span>
                <span
                    v-if="hasBadge(option)"
                    class="scope-selector__option-badge"
                    :aria-label="`count-${getOptionBadge(option)}`"
                >
                    {{ getOptionBadge(option) }}
                </span>
            </template>
        </button>
    </div>
</template>

<script setup>
import {
    ref,
    computed,
    onMounted,
    onBeforeUnmount,
    watch,
    nextTick
} from 'vue';
import Icon from './Icon.vue';

const selected = defineModel();
const emit = defineEmits(['select']);

const props = defineProps({
    options: Array,
    icons: Boolean,
    width: String,
    size: {
        type: String,
        default: 'xl'
    },
    disabled: Boolean
});

const styles = computed(() => (props.width ? { width: props.width } : null));

const selectorRef = ref(null);
const optionRefs = ref([]);
let resizeObserver = null;

function setOptionRef(el, index) {
    if (el) {
        optionRefs.value[index] = el;
    }
}

const highlightStyle = ref({});

function isSelected(option) {
    return getOptionId(option) === getOptionId(selected.value);
}

function getSelectedIndex() {
    return props.options.findIndex(isSelected);
}

function getOptionLabel(option) {
    if (option && typeof option === 'object' && 'name' in option) {
        return option.name;
    }

    return option;
}

function getOptionIcon(option) {
    if (option && typeof option === 'object' && 'icon' in option) {
        return option.icon;
    }

    return null;
}

function getOptionId(option) {
    if (option && typeof option === 'object' && 'id' in option) {
        return option.id;
    }

    return option;
}

function getOptionBadge(option) {
    if (!option || typeof option !== 'object') {
        return null;
    }

    if ('badge' in option) {
        return option.badge;
    }

    if ('count' in option) {
        return option.count;
    }

    return null;
}

function hasBadge(option) {
    const badge = getOptionBadge(option);
    return badge !== null && badge !== undefined && `${badge}` !== '';
}

function updateHighlight() {
    nextTick(() => {
        const index = getSelectedIndex();
        const el = optionRefs.value[index];

        if (el) {
            highlightStyle.value = {
                transform: `translateX(${el.offsetLeft}px)`,
                width: `${el.offsetWidth}px`
            };
        }
    });
}
function select(option) {
    selected.value = option;
    emit('select', option);
    updateHighlight();
}

onMounted(() => {
    updateHighlight();

    if (typeof ResizeObserver !== 'undefined' && selectorRef.value) {
        resizeObserver = new ResizeObserver(() => {
            updateHighlight();
        });
        resizeObserver.observe(selectorRef.value);
    }
});

watch(
    () => selected.value,
    () => {
        updateHighlight();
    }
);

onBeforeUnmount(() => {
    if (resizeObserver) {
        resizeObserver.disconnect();
        resizeObserver = null;
    }
});
</script>

<style lang="scss" scoped>
@import '../assets/styles/_main.scss';

.scope-selector {
    position: relative;
    display: inline-flex;
    width: fit-content;
    padding: 2px;
    border-radius: 10px;
    background: $card-neutral-background;

    &__highlight {
        pointer-events: none;
        position: absolute;
        top: 2px;
        bottom: 2px;
        left: 0;
        border-radius: 8px;
        background: $card-background;
        @include transition-default;
        z-index: 1;
    }

    &__option {
        position: relative;
        z-index: 2;
        color: $text-mid-emphasis;
        text-align: center;
        @include fz-14;
        font-weight: 500;
        padding: 0 8px;
        width: 100%;
        height: 100%;
        background-color: transparent;
        border-radius: 8px;
        border: none;
        outline: none;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 4px;
        justify-content: center;
        text-wrap: nowrap;
        @include transition-default(color);

        &:not(:disabled).-selected {
            color: $text-high-emphasis;
        }

        &:disabled {
            cursor: not-allowed;
        }

        &.-iconic {
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 0 6px;
        }
    }

    &__option-label {
        white-space: nowrap;
    }

    &__option-icon {
        width: 16px;
        height: 16px;
    }

    &__option-badge {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 20px;
        width: auto;
        padding: 0 6px;
        height: 20px;
        border-radius: 6px;
        background: $app-background;
        color: $text-mid-emphasis;
        @include fz-12;
        font-weight: 500;
        line-height: 1;
        white-space: nowrap;
        @include transition-default;
    }

    &__option.-selected &__option-badge {
        background: $card-primary-background-light;
        color: $primary-deep;
    }

    &.-xl {
        height: 40px;
    }

    &.-lg {
        height: 36px;
    }

    &.-md {
        height: 32px;
    }

    &.-sm {
        height: 28px;

        .-iconic {
            padding: 0 4px;
        }
    }
}

body[theme='dark'] .scope-selector {
    background: $background-secondary;
    border: 1px solid $border-secondary;

    &__highlight {
        background-color: $background-tertiary;
    }

    &__option {
        color: $text-mid-emphasis;

        &:hover:not(.-selected) {
            color: $text-high-emphasis;
        }

        &:not(:disabled).-selected {
            color: $text-high-emphasis;
        }
    }
}

@media (min-width: 768px) {
    .scope-selector {
        &__option {
            &:hover:not(.-selected) {
                color: $text-mid-emphasis !important;
            }

            &.-selected:hover {
                color: $text-high-emphasis !important;
            }
        }
    }
    body[theme='dark'] .scope-selector {
        &__option {
            &:hover:not(.-selected) {
                color: $text-mid-emphasis !important;
            }

            &.-selected:hover {
                color: $text-high-emphasis !important;
            }
        }
    }
}
</style>
